import { z } from 'zod';
import { discountCodeSchema } from './promotions/discountCode.js';

/** Validation limits for MarketingAttribution fields. */
const MARKETING_ATTRIBUTION_LIMITS = {
    CLICK_ID_MAX: 500,
    UTM_MAX: 200,
    LANDING_PAGE_MAX: 2000,
    CLIENT_IP_MAX: 45,
    USER_AGENT_MAX: 500,
    META_FBP_MAX: 500,
    // _fbc embeds the fbclid query parameter: fb.1.<timestamp>.<fbclid>
    // fbclid values can exceed 500 chars, so this limit is higher than _fbp.
    META_FBC_MAX: 1000,
    GA_CLIENT_ID_MAX: 200,
    LINK_SOURCE_MAX: 100,
    // Cap at 64 to comfortably hold a UUID v4 (36) plus headroom for future
    // formats (hashed alternates, prefixed envs). Tight enough to reject
    // junk values from forged cookies.
    IDENTITY_MAX: 64,
    // Affiliate codes share the promotion-code alphabet. The path that issues
    // affiliate codes must enforce these same bounds so every code parses here.
    AFFILIATE_CODE_MIN: 3,
    AFFILIATE_CODE_MAX: 40,
};
const { CLICK_ID_MAX, UTM_MAX, LANDING_PAGE_MAX, CLIENT_IP_MAX, USER_AGENT_MAX, META_FBP_MAX, META_FBC_MAX, GA_CLIENT_ID_MAX, LINK_SOURCE_MAX, IDENTITY_MAX, AFFILIATE_CODE_MIN, AFFILIATE_CODE_MAX, } = MARKETING_ATTRIBUTION_LIMITS;
/**
 * How the credited affiliate landed on the cart. LINK is a `?ref=` (or
 * `affiliateCode`) capture; CODE is a typed discount code. Typed code wins
 * at placement when both happened.
 */
const AFFILIATE_ATTRIBUTION_VIA = {
    LINK: 'LINK',
    CODE: 'CODE',
};
const affiliateAttributionViaValues = [
    AFFILIATE_ATTRIBUTION_VIA.LINK,
    AFFILIATE_ATTRIBUTION_VIA.CODE,
];
/**
 * Leak flags stamped at order placement. Shown in admin, never enforced.
 * CODE_AFTER_REJECTED_BURST: accepted within 10s of 3+ failed codes.
 * CODE_WITHOUT_LINK: CODE credit with no prior link touch.
 */
const AFFILIATE_ATTRIBUTION_FLAG = {
    CODE_AFTER_REJECTED_BURST: 'CODE_AFTER_REJECTED_BURST',
    CODE_WITHOUT_LINK: 'CODE_WITHOUT_LINK',
};
const affiliateAttributionFlagValues = [
    AFFILIATE_ATTRIBUTION_FLAG.CODE_AFTER_REJECTED_BURST,
    AFFILIATE_ATTRIBUTION_FLAG.CODE_WITHOUT_LINK,
];
const affiliateCodeSchema = discountCodeSchema({
    min: AFFILIATE_CODE_MIN,
    max: AFFILIATE_CODE_MAX,
});
/**
 * A `?ref=` or `affiliateCode` value as a client sent it. Loose on purpose:
 * a malformed code is ignored by the resolver, and must not reject the cart
 * request that carried it.
 */
const affiliateCodeRequestSchema = z.string().trim().optional();
/**
 * When the client first captured the `?ref=` it sends, in epoch ms. A revived
 * cookie code keeps its original capture time, so the attribution window runs
 * from the referral, not the return visit. Loose like the code: a bad value is
 * dropped rather than rejecting the request.
 */
const affiliateCodeCapturedAtRequestSchema = z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined);
/**
 * One affiliate touch. `via` is the carrier: LINK for a captured `?ref=`, CODE
 * for a typed discount code. A touch without it is a link, the only carrier
 * recorded before typed codes were.
 */
const marketingAffiliateTouchSchema = z
    .object({
    code: affiliateCodeSchema,
    at: z.number().int(),
    via: z.enum(affiliateAttributionViaValues).optional(),
})
    .strict();
/**
 * Affiliate credit on a CheckoutCart or PlacedOrder. One credited
 * `affiliateId` (last touch inside that affiliate's window; typed code wins).
 * Remove-credit clears `affiliateId` and keeps the raw `code`.
 */
const marketingAffiliateAttributionSchema = z
    .object({
    code: affiliateCodeSchema,
    affiliateId: z.string().min(1).optional(),
    affiliateName: z.string().max(UTM_MAX).optional(),
    via: z.enum(affiliateAttributionViaValues),
    touches: z.array(marketingAffiliateTouchSchema).min(1),
    flags: z.array(z.enum(affiliateAttributionFlagValues)).optional(),
})
    .strict();
/**
 * Sentinel `affiliate.affiliateId` on the orders search denorm when the order
 * has no credited affiliate. Indexed so the Orders "No affiliate" filter can
 * equality-match; Typesense has no "field is absent" operator.
 */
const ORDERS_AFFILIATE_NONE = '__none__';
/**
 * Build the orders-search affiliate denorm from cart/order attribution.
 * Uncredited (or credit-cleared) attribution yields the none sentinel.
 */
function buildOrdersAffiliateSearchFields(attribution) {
    const affiliate = attribution?.affiliate;
    if (!affiliate?.affiliateId) {
        // A window re-check can clear the credit and keep leak flags, which the
        // column shows alongside None.
        return {
            affiliateId: ORDERS_AFFILIATE_NONE,
            ...(affiliate?.code !== undefined ? { code: affiliate.code } : {}),
            ...(affiliate?.flags !== undefined && affiliate.flags.length > 0
                ? { flags: affiliate.flags }
                : {}),
        };
    }
    return {
        affiliateId: affiliate.affiliateId,
        ...(affiliate.affiliateName !== undefined ? { name: affiliate.affiliateName } : {}),
        via: affiliate.via,
        ...(affiliate.flags !== undefined && affiliate.flags.length > 0
            ? { flags: affiliate.flags }
            : {}),
        code: affiliate.code,
    };
}
/** Human label for a leak flag shown in the orders table and drawer. */
function labelForAffiliateAttributionFlag(flag) {
    switch (flag) {
        case AFFILIATE_ATTRIBUTION_FLAG.CODE_AFTER_REJECTED_BURST:
            return 'Code accepted after a burst of rejected codes';
        case AFFILIATE_ATTRIBUTION_FLAG.CODE_WITHOUT_LINK:
            return 'Discount code used without an affiliate link';
        default: {
            const exhaustive = flag;
            return exhaustive;
        }
    }
}
/** Human label for how the affiliate was referred (drawer "Referred via"). */
function labelForAffiliateAttributionVia(via) {
    switch (via) {
        case AFFILIATE_ATTRIBUTION_VIA.LINK:
            return 'Affiliate link';
        case AFFILIATE_ATTRIBUTION_VIA.CODE:
            return 'Discount code';
        default: {
            const exhaustive = via;
            return exhaustive;
        }
    }
}
/**
 * Zod schema for MarketingAttribution — single source of truth.
 * All fields are optional since partial data (e.g., only gclid, no UTMs) is valid.
 */
const marketingAttributionSchema = z
    .object({
    /** Google click ID */
    gclid: z.string().max(CLICK_ID_MAX).optional(),
    /** Google broad-match click ID (app-to-web) */
    gbraid: z.string().max(CLICK_ID_MAX).optional(),
    /** Google broad-match click ID (web-to-app) */
    wbraid: z.string().max(CLICK_ID_MAX).optional(),
    /** Facebook click ID */
    fbclid: z.string().max(CLICK_ID_MAX).optional(),
    /** Microsoft click ID */
    msclkid: z.string().max(CLICK_ID_MAX).optional(),
    /** TikTok click ID */
    ttclid: z.string().max(CLICK_ID_MAX).optional(),
    /** UTM source parameter */
    utmSource: z.string().max(UTM_MAX).optional(),
    /** UTM medium parameter */
    utmMedium: z.string().max(UTM_MAX).optional(),
    /** UTM campaign parameter */
    utmCampaign: z.string().max(UTM_MAX).optional(),
    /** UTM content parameter */
    utmContent: z.string().max(UTM_MAX).optional(),
    /** UTM term parameter */
    utmTerm: z.string().max(UTM_MAX).optional(),
    /** UTM ID parameter */
    utmId: z.string().max(UTM_MAX).optional(),
    /** Full URL the buyer first arrived on */
    landingPage: z.string().url().max(LANDING_PAGE_MAX).optional(),
    /** Timestamp of when params were captured (cookie write time) */
    capturedAt: z.number().int().optional(),
    /** Client IP address captured server-side from X-Forwarded-For (IPv6 max 45 chars) */
    clientIp: z.string().max(CLIENT_IP_MAX).optional(),
    /** User agent string captured server-side from request headers */
    userAgent: z.string().max(USER_AGENT_MAX).optional(),
    /** Meta first-party browser ID cookie (_fbp) */
    fbp: z.string().max(META_FBP_MAX).optional(),
    /** Meta fbclid cookie (_fbc) */
    fbc: z.string().max(META_FBC_MAX).optional(),
    /** GA4 client ID from _ga cookie */
    gaClientId: z.string().max(GA_CLIENT_ID_MAX).optional(),
    /** Page referrer URL at checkout entry */
    referrer: z.string().url().max(LANDING_PAGE_MAX).optional(),
    /** Source of dealer-generated link (email, sms, direct, etc.) */
    linkSource: z.string().max(LINK_SOURCE_MAX).optional(),
    /**
     * First-party anonymous buyer ID (cookie `ekho_aid`, 2-year lifetime).
     * Stable across visits on the host domain; used as a join key for
     * cart ↔ buyer-behavior ↔ eventual order without ever requiring PII.
     * SDK-owned UUID, not borrowed from any analytics vendor.
     */
    anonymousId: z.string().max(IDENTITY_MAX).optional(),
    /**
     * Session-scoped ID (cookie `ekho_sid`, 30-minute sliding window).
     * Groups page views / cart attempts / chat turns into a single visit.
     * Matches GA4 / Segment session semantics.
     */
    sessionId: z.string().max(IDENTITY_MAX).optional(),
    /**
     * Affiliate credit for this cart or order. Optional so existing
     * documents keep parsing; `.strict()` on this parent schema is why the
     * field must exist before any writer sends it.
     */
    affiliate: marketingAffiliateAttributionSchema.optional(),
})
    .strict();
/**
 * Attribution a client may send on a request body. Omits `affiliate` and
 * stays strict, so a client-sent affiliate credit fails validation: only the
 * server writes `affiliate`, after resolving the code. Stored documents use
 * the full `marketingAttributionSchema`.
 */
const marketingAttributionRequestSchema = marketingAttributionSchema.omit({
    affiliate: true,
});
/** Click ID field names — last-touch semantics (newest value always overwrites). */
const CLICK_ID_FIELDS = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
    'ttclid',
];
/** UTM field names — first-touch semantics (earliest non-undefined value wins). */
const UTM_FIELDS = [
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'utmContent',
    'utmTerm',
    'utmId',
];
/** Tuples mapping UTM URL param names to MarketingAttribution field names. */
const UTM_URL_PARAM_ENTRIES = [
    ['utm_source', 'utmSource'],
    ['utm_medium', 'utmMedium'],
    ['utm_campaign', 'utmCampaign'],
    ['utm_content', 'utmContent'],
    ['utm_term', 'utmTerm'],
    ['utm_id', 'utmId'],
];
/** Maps MarketingAttribution field names to URL parameter names. Used for URL forwarding. */
const FIELD_TO_URL_PARAM_ENTRIES = [
    ['gclid', 'gclid'],
    ['gbraid', 'gbraid'],
    ['wbraid', 'wbraid'],
    ['fbclid', 'fbclid'],
    ['msclkid', 'msclkid'],
    ['ttclid', 'ttclid'],
    ['utmSource', 'utm_source'],
    ['utmMedium', 'utm_medium'],
    ['utmCampaign', 'utm_campaign'],
    ['utmContent', 'utm_content'],
    ['utmTerm', 'utm_term'],
    ['utmId', 'utm_id'],
];
/** String fields on MarketingAttribution — derived from click ID + UTM + landing page + server-captured fields. */
const MARKETING_STRING_FIELDS = [
    ...CLICK_ID_FIELDS,
    ...UTM_FIELDS,
    'landingPage',
    'clientIp',
    'userAgent',
    'fbp',
    'fbc',
    'gaClientId',
    'referrer',
    'linkSource',
    'anonymousId',
    'sessionId',
];
/**
 * Runtime type guard for MarketingAttribution.
 * Validates that the value is a non-array object with correctly typed fields.
 */
function isMarketingAttribution(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return false;
    const obj = value;
    const affiliateOk = obj.affiliate === undefined ||
        marketingAffiliateAttributionSchema.safeParse(obj.affiliate).success;
    return (MARKETING_STRING_FIELDS.every((f) => obj[f] === undefined || typeof obj[f] === 'string') &&
        (obj.capturedAt === undefined || typeof obj.capturedAt === 'number') &&
        affiliateOk);
}

export { AFFILIATE_ATTRIBUTION_FLAG, AFFILIATE_ATTRIBUTION_VIA, CLICK_ID_FIELDS, FIELD_TO_URL_PARAM_ENTRIES, MARKETING_ATTRIBUTION_LIMITS, ORDERS_AFFILIATE_NONE, UTM_FIELDS, UTM_URL_PARAM_ENTRIES, affiliateCodeCapturedAtRequestSchema, affiliateCodeRequestSchema, affiliateCodeSchema, buildOrdersAffiliateSearchFields, isMarketingAttribution, labelForAffiliateAttributionFlag, labelForAffiliateAttributionVia, marketingAffiliateAttributionSchema, marketingAffiliateTouchSchema, marketingAttributionRequestSchema, marketingAttributionSchema };
//# sourceMappingURL=MarketingAttribution.js.map
